import { Routes, Route, Navigate } from 'react-router-dom'
import { useHideAddressBar } from '@/hooks/useHideAddressBar'
import { lazy, Suspense } from 'react'

// 즉시 로드: 로그인/대시보드(첫 진입 핫패스) + 레이아웃/가드
import LoginPage from '@/pages/auth/LoginPage'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import AdminLayout from '@/layouts/AdminLayout'
import AuthGuard from '@/components/auth/AuthGuard'

// ── 나머지 전 페이지 lazy 분할: 2.7MB 통짜 번들이 첫 로딩/새로고침 렉의 주원인 ──
// (관리자 페이지는 전부 진입 시점 로드로 충분 — 청크는 방문 후 브라우저 캐시)
const UsersPage = lazy(() => import('@/pages/users/UsersPage'))
const DepositsWithdrawalsManagementPage = lazy(() => import('@/pages/deposits-withdrawals/DepositsWithdrawalsManagementPage'))
const DepositsHistoryPage = lazy(() => import('@/pages/deposits-history/DepositsHistoryPage'))
const WithdrawalsHistoryPage = lazy(() => import('@/pages/withdrawals-history/WithdrawalsHistoryPage'))
const MoneyLogPage = lazy(() => import('@/pages/money-log/MoneyLogPage'))
const PartnerBonusPage = lazy(() => import('@/pages/partner-bonus/PartnerBonusPage'))
const DailyStatisticsPage = lazy(() => import('@/pages/statistics/DailyStatisticsPage'))
const UserStatisticsPage = lazy(() => import('@/pages/statistics/UserStatisticsPage'))
const PageAnalyticsPage = lazy(() => import('@/pages/page-analytics/PageAnalyticsPage'))
const TradingPage = lazy(() => import('@/pages/trading/TradingPage'))
const MarketsPage = lazy(() => import('@/pages/markets/MarketsPage'))
const ChatPage = lazy(() => import('@/pages/chat/ChatPage'))
const InquiriesPage = lazy(() => import('@/pages/inquiries/InquiriesPage'))
const ContentPage = lazy(() => import('@/pages/content/ContentPage'))
const NoticesPage = lazy(() => import('@/pages/notices/NoticesPage'))
const NoticeConsentsPage = lazy(() => import('@/pages/notices/NoticeConsentsPage'))
const TemplatesPage = lazy(() => import('@/pages/templates/TemplatesPage'))
const AccessLogsPage = lazy(() => import('@/pages/access-logs/AccessLogsPage'))
const OnlineMembersPage = lazy(() => import('@/pages/online-members/OnlineMembersPage'))
const IpBlocksPage = lazy(() => import('@/pages/ip-blocks/IpBlocksPage'))
const DeviceBlocksPage = lazy(() => import('@/pages/device-blocks/DeviceBlocksPage'))
const EventLogsPage = lazy(() => import('@/pages/event-logs/EventLogsPage'))
const HtsLogsPage = lazy(() => import('@/pages/hts-logs/HtsLogsPage'))
const HtsAppUsagePage = lazy(() => import('@/pages/hts-logs/HtsAppUsagePage'))
const ConsultationsPage = lazy(() => import('@/pages/consultations/ConsultationsPage'))
const HandoverPage = lazy(() => import('@/pages/handover/HandoverPage'))
const PromotionsPage = lazy(() => import('@/pages/promotions/PromotionsPage'))
const PromotionDetailPage = lazy(() => import('@/pages/promotions/PromotionDetailPage'))
const ManualRequestsPage = lazy(() => import('@/pages/manual-requests/ManualRequestsPage'))
const ServiceBlockPage = lazy(() => import('@/pages/service-block/ServiceBlockPage'))
const PartnersPage = lazy(() => import('@/pages/partners/PartnersPage'))
const PartnerWithdrawalsPage = lazy(() => import('@/pages/partners/PartnerWithdrawalsPage'))
const PartnerFundsPage = lazy(() => import('@/pages/partners/PartnerFundsPage'))
const PartnerDeductionLogsPage = lazy(() => import('@/pages/partners/PartnerDeductionLogsPage'))
const StakingPage = lazy(() => import('@/pages/staking/StakingPage'))
const PopupAdsPage = lazy(() => import('@/pages/popup-ads/PopupAdsPage'))
const EventsPage = lazy(() => import('@/pages/events/EventsPage'))
const CompetitionsPage = lazy(() => import('@/pages/competitions/CompetitionsPage'))
const CompetitionDetailPage = lazy(() => import('@/pages/competitions/CompetitionDetailPage'))
const AdminMessagesPage = lazy(() => import('@/pages/messages/AdminMessagesPage'))
const PartnerInquiriesPage = lazy(() => import('@/pages/partner-inquiries/PartnerInquiriesPage'))
const MembershipTiersPage = lazy(() => import('@/pages/membership-tiers/MembershipTiersPage'))
const TermsManagementPage = lazy(() => import('@/pages/terms/TermsManagementPage'))
const PositionsPage = lazy(() => import('@/pages/positions/PositionsPage'))
const TradingSettingsPage = lazy(() => import('@/pages/trading-settings/TradingSettingsPage'))
const MissionsPage = lazy(() => import('@/pages/missions/MissionsPage'))
const MissionStatisticsPage = lazy(() => import('@/pages/missions/MissionStatisticsPage'))
const StakingHistoryPage = lazy(() => import('@/pages/staking-history/StakingHistoryPage'))
const PositionHistoryPage = lazy(() => import('@/pages/position-history/PositionHistoryPage'))
const MessageHistoryPage = lazy(() => import('@/pages/message-history/MessageHistoryPage'))
const BalanceTrackingPage = lazy(() => import('@/pages/balance-tracking/BalanceTrackingPage'))
const ShareCardSettingsPage = lazy(() => import('@/pages/settings/ShareCardSettings'))
const FAQsPage = lazy(() => import('./pages/faqs/FAQsPage'))
const SystemHealthPage = lazy(() => import('./pages/system-health/SystemHealthPage'))
const WithdrawalRecordToolPage = lazy(() => import('@/pages/withdrawal-record-tool/WithdrawalRecordToolPage'))
const DemoUsersPage = lazy(() => import('@/pages/demo-users/DemoUsersPage'))
const DemoDepositsPage = lazy(() => import('@/pages/demo-deposits/DemoDepositsPage'))

import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  registerServiceWorker,
  ensurePushSubscription,
  setupSwNavigationListener,
  isPushSupported,
} from '@/services/pushNotificationService'

function AppAdmin() {
  const { t } = useTranslation('common')
  // 모바일에서 주소창 숨기기
  useHideAddressBar()

  // Web Push: SW 등록 + 알림 클릭 → 라우팅 + 권한 있을 때 자동 구독
  const navigate = useNavigate()
  useEffect(() => {
    if (!isPushSupported()) return
    let cancelled = false
    ;(async () => {
      await registerServiceWorker()
      if (cancelled) return
      setupSwNavigationListener((url: string) => navigate(url))
      // 이미 권한 받은 사용자는 자동 재구독 (브라우저 캐시 클리어/SW 갱신 후에도 안전)
      ensurePushSubscription().catch(() => {})
    })()
    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="bg-dark-950 overflow-hidden h-app" style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)'
    }}>
      {/* lazy 청크 로딩 폴백 — 보통 수십 ms, 이후 캐시 */}
      <Suspense fallback={
        <div className="flex items-center justify-center h-full py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      }>
      <Routes>
        {/* Admin Login */}
        <Route path="/login" element={<LoginPage />} />

        {/* Admin routes (nested) */}
        <Route path="/admin" element={<AuthGuard><AdminLayout /></AuthGuard>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="users/*" element={<UsersPage />} />
          {/* 모의투자 계정 관리 (2026-08-08) — 상세는 기존 /admin/users/:id 재사용 */}
          <Route path="demo-users/*" element={<DemoUsersPage />} />
          <Route path="demo-deposits" element={<DemoDepositsPage />} />
          <Route path="access-logs" element={<AccessLogsPage />} />
          <Route path="online-members" element={<OnlineMembersPage />} />
          <Route path="ip-blocks" element={<IpBlocksPage />} />
          <Route path="device-blocks" element={<DeviceBlocksPage />} />
          <Route path="event-logs" element={<EventLogsPage />} />
          <Route path="hts-logs" element={<HtsLogsPage />} />
          <Route path="hts-app-usage" element={<HtsAppUsagePage />} />
          <Route path="consultations" element={<ConsultationsPage />} />
          <Route path="handover" element={<HandoverPage />} />
          <Route path="promotions" element={<PromotionsPage />} />
          <Route path="promotions/:id" element={<PromotionDetailPage />} />
          <Route path="manual-requests" element={<ManualRequestsPage />} />
          <Route path="service-block" element={<ServiceBlockPage />} />
          <Route path="deposits" element={<Navigate to="/admin/deposits-history" replace />} />
          <Route path="withdrawals" element={<Navigate to="/admin/withdrawals-history" replace />} />
          <Route path="deposits-withdrawals-management" element={<DepositsWithdrawalsManagementPage />} />
          <Route path="deposits-history" element={<DepositsHistoryPage />} />
          <Route path="withdrawals-history" element={<WithdrawalsHistoryPage />} />
          <Route path="money-log" element={<MoneyLogPage />} />
          <Route path="partner-bonus" element={<PartnerBonusPage />} />
          <Route path="daily-statistics" element={<DailyStatisticsPage />} />
          <Route path="user-statistics" element={<UserStatisticsPage />} />
          <Route path="page-analytics" element={<PageAnalyticsPage />} />
          <Route path="trading" element={<TradingPage />} />
          <Route path="markets" element={<MarketsPage />} />
          <Route path="positions" element={<PositionsPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="inquiries" element={<InquiriesPage />} />
          <Route path="content/*" element={<ContentPage />} />
          <Route path="notices" element={<NoticesPage />} />
          <Route path="notice-consents" element={<NoticeConsentsPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="partner-funds" element={<PartnerFundsPage />} />
          <Route path="partner-withdrawals" element={<PartnerWithdrawalsPage />} />
          <Route path="partner-deduction-logs" element={<PartnerDeductionLogsPage />} />
          <Route path="staking" element={<StakingPage />} />
          <Route path="popup-ads" element={<PopupAdsPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="competitions" element={<CompetitionsPage />} />
          <Route path="competitions/:id" element={<CompetitionDetailPage />} />
          <Route path="messages" element={<AdminMessagesPage />} />
          <Route path="faqs" element={<FAQsPage />} />
          <Route path="partner-inquiries" element={<PartnerInquiriesPage />}/>
          <Route path="membership-tiers" element={<MembershipTiersPage />}/>
          <Route path="terms" element={<TermsManagementPage />} />
          <Route path="mission-statistics" element={<MissionStatisticsPage />} />
          <Route path="missions" element={<MissionsPage />} />
          <Route path="trading-settings" element={<TradingSettingsPage />} />
          <Route path="staking-history" element={<StakingHistoryPage />} />
          <Route path="position-history" element={<PositionHistoryPage />} />
          <Route path="message-history" element={<MessageHistoryPage />} />
          <Route path="balance-tracking" element={<BalanceTrackingPage />} />
          <Route path="share-card-settings" element={<ShareCardSettingsPage />} />
          {/* 신선물 관리 → 통합 시장관리 해외 탭 (2026-10-06) */}
          <Route path="nf-management" element={<Navigate to="/admin/markets?tab=OVERSEAS" replace />} />
          {/* 숨겨진 페이지 - 사이드바에 표시되지 않음, URL 직접 접근만 가능 */}
          <Route path="system-health" element={<SystemHealthPage />} />
          {/* 출금 내역 생성 도구 (2026-08-11) — URL 직접접근 전용 + 비밀번호 게이트 */}
          <Route path="withdrawal-record-tool" element={<WithdrawalRecordToolPage />} />
        </Route>

        {/* Default redirects to admin */}
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/users" element={<Navigate to="/admin/users" replace />} />
        <Route path="/deposits" element={<Navigate to="/admin/deposits" replace />} />
        <Route path="/withdrawals" element={<Navigate to="/admin/withdrawals" replace />} />
        <Route path="/trading" element={<Navigate to="/admin/trading" replace />} />
        <Route path="/markets" element={<Navigate to="/admin/markets" replace />} />
        <Route path="/positions" element={<Navigate to="/admin/positions" replace />} />
        <Route path="/chat" element={<Navigate to="/admin/chat" replace />} />
        <Route path="/inquiries" element={<Navigate to="/admin/inquiries" replace />} />
        <Route path="/content" element={<Navigate to="/admin/content" replace />} />
        <Route path="/notices" element={<Navigate to="/admin/notices" replace />} />
        <Route path="/partners" element={<Navigate to="/admin/partners" replace />} />
        <Route path="/partner-withdrawals" element={<Navigate to="/admin/partner-withdrawals" replace />} />
        <Route path="/staking" element={<Navigate to="/admin/staking" replace />} />
        <Route path="/popup-ads" element={<Navigate to="/admin/popup-ads" replace />} />
        <Route path="/events" element={<Navigate to="/admin/events" replace />} />
        <Route path="/messages" element={<Navigate to="/admin/messages" replace />} />
        <Route path="/faqs" element={<Navigate to="/admin/faqs" replace />} />
        <Route path="/partner-inquiries" element={<Navigate to="/admin/partner-inquiries" replace />}/>
        <Route path="/membership-tiers" element={<Navigate to="/admin/membership-tiers" replace />}/>
        <Route path="/terms" element={<Navigate to="/admin/terms" replace />} />
        <Route path="/missions" element={<Navigate to="/admin/missions" replace />} />
        {/* Catch all - redirect to admin dashboard */}
        <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
      </Suspense>
    </div>
  )
}

export default AppAdmin